// moms-harness:anchor v2
// JIRA: LOGISPRD-13833, LOGISPRD-13840, LOGISPRD-13841
import React, { useEffect, useState } from 'react'
import { Input, type PboTableColumn } from '@musinsa/pbo-core'
import { NUMERIC_COLUMNS } from './consts'
import { toPriceOrNull } from './toPriceOrNull'
import { priceToText, sanitizePriceText } from './priceText'

interface PriceCellProps {
  /** 행 값. `null` 이어도 빈 입력칸을 그린다 */
  value: number | null | undefined
  /** 편집 텍스트. 숫자 변환은 호출부(`onCellChange` → `toPriceOrNull`)가 한다 */
  onChange: (text: string) => void
}

/**
 * MA18 단가 셀 — pbo-core `amountInput` 대신 쓴다 (이유는 [sanitizePriceText] KDoc).
 *
 * ### 입력 중 텍스트를 셀이 따로 쥔다
 *
 * 행에는 `number | null` 로 저장되므로 행 값만 그리면 `"12."` 가 `12` 로 되돌아가 **소수점을 칠 수
 * 없다.** 그래서 입력 중 텍스트(`draft`)를 셀이 들고, 행 값과 숫자가 다를 때만 행 값으로 맞춘다 —
 * 저장 후 재조회·엑셀 업로드처럼 **바깥에서 값이 바뀐 경우**다.
 */
export function PriceCell({ value, onChange }: Readonly<PriceCellProps>) {
  const [draft, setDraft] = useState(() => priceToText(value))

  useEffect(() => {
    setDraft((prev) => (toPriceOrNull(prev) === (value ?? null) ? prev : priceToText(value)))
  }, [value])

  return (
    <Input
      value={draft}
      inputMode="decimal"
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
        const next = sanitizePriceText(e.target.value)
        setDraft(next)
        onChange(next)
      }}
    />
  )
}

/**
 * 단가 열([NUMERIC_COLUMNS])에만 [PriceCell] 렌더를 끼운다. 다른 열은 그대로 둔다.
 *
 * pbo-core 는 컬럼에 `render` 가 있으면 variant 렌더러보다 우선한다(`render ?? variant`) — DS 가
 * 화면 특화 셀을 위해 열어 둔 탈출구다. `consts.ts` 의 단가 두 열은 **variant 를 두지 않는다** —
 * 남겨 두면 이 함수 없이 `COLUMNS` 를 쓰는 순간 amountInput 이 조용히 되살아난다. 편집 결과는 다른
 * 열과 **같은 `onCellChange`** 로 흘러 숫자 변환(`toPriceOrNull`)과 검증(`validateRow`)이 한 경로에 남는다.
 */
export const withPriceCells = (columns: PboTableColumn[], onChange: (rowKey: string, dataIndex: string, text: string) => void): PboTableColumn[] =>
  columns.map((column) => {
    const { dataIndex } = column
    if (!dataIndex || !NUMERIC_COLUMNS.has(dataIndex)) return column
    return {
      ...column,
      render: (value, row) => {
        // `row` 는 `Record<string, unknown>` 이라 행 키가 보장되지 않는다. `String(undefined)` 로 넘기면
        // `onCellChange` 가 'undefined' 행을 못 찾아 **편집이 조용히 사라진다** — 키가 없으면 넘기지 않는다.
        // 지금 데이터 흐름에서는 닿지 않는 분기라, 닿았다면 호출부 실수다. 개발 빌드에서만 알린다
        // (`import.meta.env.DEV` 는 vite 가 컴파일타임에 치환해 운영 빌드에서는 분기째 빠진다)
        const rowKey = row._rowKey
        return (
          <PriceCell
            value={value as number | null | undefined}
            onChange={(text) => {
              if (typeof rowKey === 'string' && rowKey) {
                onChange(rowKey, dataIndex, text)
              } else if (import.meta.env.DEV) {
                // 개발 빌드 전용 진단이다 — 운영 빌드에서는 이 분기가 빠진다
                // eslint-disable-next-line no-console
                console.warn('[PriceCell] _rowKey 가 없어 편집을 버렸습니다', { dataIndex, rowKey })
              }
            }}
          />
        )
      },
    }
  })
